"use client"

import { SiteHeader } from "@/components/site-header"
import { SiteFooter } from "@/components/site-footer"
import { getProxiedStoragePath } from "@/lib/proxied-storage-path"
import { SafeImage } from "@/components/ui/safe-image"
import Link from "next/link"
import { editorialStandards } from "@/lib/editorial-standards"
import { FeaturedInBand } from "@/components/press/featured-in-band"
import { CheckCircle2, ArrowRight } from "lucide-react"
import { ToolShareBar } from "@/components/brand-kit/tool-share-bar"
import { CTABand } from "@/components/brand-kit/cta-band"
import { SITE_STATS, RETAILER_INTEGRATIONS_DISPLAY } from "@/lib/site-stats"
import { TRUST_SIGNALS } from "@/lib/trust-signals"
import { SOCIAL_MEDIA_PROFILES } from "@/lib/social-media-config"
import {
  ORGANIZATION_ID,
  PUBLISHING_PRINCIPLES_PATH,
  SITE_ORIGIN,
  SITE_TAGLINE,
  WEBSITE_ID,
  contactPointNode,
  knowsAboutNodes,
} from "@/lib/site-entity"
import { buildIdentifierTermSetNode } from "@/lib/seo/fin-parts-jsonld"
import { SystemsComparisonTable } from "./_components/systems-table"
import { ExploreVisual } from "./_components/explore-visual"
import { ensureAltBranding } from "@/lib/branding-enforcement"
import type { AuthorProfile } from "@/lib/blog-data"
import { identifierDefinitionSentence } from "@/lib/identifiers"
import { FIN_NOT_ACCOUNT_OR_ORDER, INDEPENDENTLY_OWNED } from "@/lib/brand-facts"
import { safeJsonLd } from '@/lib/json-ld'

/* ---------------------------------------------------------------------------
 * DATA — every value below is live-derived from the Fridge.com production
 * database (the Fridge production Supabase) and verified 07/20/2026.
 * See the report / docs/Operations.md for the audit trail.
 *
 * AUTHORS — READ BEFORE TOUCHING THE #authors SECTION.
 * Two owner rulings point opposite ways and neither has been withdrawn:
 *   08/03/2026 (FRIDGE-131): the byline is organizational only; the four
 *     invented editor personas were removed from this page. Its closing
 *     condition #4 is "/about-us no longer claims four real named editors."
 *   08/06/2026 (PR #662): person bylines RESTORED on the owner's direct
 *     order — they render on ~18,398 article pages today.
 *   08/21/2026: the owner asked for authors with images on this page.
 * The section below is built and rendering, but it is OWNER GATED: it shows
 * only what the authors table already publishes as visible bylines, adds no
 * authorship sentence, and emits nothing to crawlers. See
 * docs/Page-Types-And-Templates.md §0 (08/21 About Us addendum) before
 * changing it either way.
 * ------------------------------------------------------------------------- */


const exploreFridgeLinks: { href: string; title: string; blurb: string }[] = [
  { href: "/best-refrigerators", title: "Best refrigerators", blurb: "Top picks by category and budget" },
  { href: "/best-freezers", title: "Best freezers", blurb: "Chest, upright, and garage-ready" },
  { href: "/refrigerator-buying-guide", title: "Refrigerator buying guide", blurb: "How to choose and size your fridge" },
  { href: "/energy-efficiency-guide", title: "Energy efficiency guide", blurb: "ENERGY STAR and operating costs" },
  { href: "/tools", title: "Tools & calculators", blurb: `${SITE_STATS.tools} free interactive tools` },
  { href: "/local", title: "Local fridge finder", blurb: `Rates and tips across ${SITE_STATS.cities} cities` },
  { href: "/rebates", title: "Utility rebates", blurb: "Purchase and recycling incentives by state" },
  { href: "/recycling-guide", title: "Recycling guide", blurb: "Verified programs and disposal options" },
  { href: "/parts", title: "Parts catalog", blurb: "Gaskets, seals, sensors, and hardware by FPN" },
  { href: "/filters", title: "Filter catalog", blurb: "Water, air, and inline filters by FFN" },
  { href: "/compare", title: "Compare products", blurb: "Side-by-side specs and prices" },
  { href: "/blogs/news", title: "News & articles", blurb: `${SITE_STATS.articles} guides and comparisons` },
  { href: "/brands", title: "Brand directory", blurb: `Shop across ${SITE_STATS.brands} brands` },
  { href: "/sitemap", title: "Sitemap", blurb: "Every Fridge.com page type, mapped" },
]


/* ---------------------------------------------------------------------------
 * SCHEMA — Organization + AboutPage + the identifier DefinedTermSet +
 * BreadcrumbList. sameAs is the real @fridgedotcom social profiles. No Person
 * nodes here: the visible author grid is rendered from the authors table, and
 * article schema carries the organizational author (FRIDGE-131).
 * ------------------------------------------------------------------------- */
// FRIDGE-795 — the @ids, tagline, knowsAbout and contactPoint are the shared
// entity constants (lib/site-entity.ts): this node and the layout's
// Organization merge on @id, so they must say the same things.
const ORG_ID = ORGANIZATION_ID

const schemaGraph = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": ORG_ID,
      name: "Fridge.com",
      url: SITE_ORIGIN,
      logo: `${SITE_ORIGIN}/Fridge.com-brand-logos/Fridge.com-blue.png`,
      description:
        "Fridge.com is an independent U.S. refrigerator and freezer search engine: curated products with regularly updated multi-retailer pricing, ENERGY STAR– and DOE-verified specifications, CPSC recall monitoring, local electricity context, utility rebates, recycling programs, and free calculators. Always free to use; funded by affiliate commissions that never affect rankings.",
      slogan: SITE_TAGLINE,
      foundingDate: "2021",
      sameAs: SOCIAL_MEDIA_PROFILES,
      address: { "@type": "PostalAddress", addressCountry: "US" },
      contactPoint: contactPointNode(SITE_ORIGIN),
      publishingPrinciples: `${SITE_ORIGIN}${PUBLISHING_PRINCIPLES_PATH}`,
      knowsAbout: knowsAboutNodes(),
    },
    {
      "@type": "AboutPage",
      "@id": `${SITE_ORIGIN}/about-us#webpage`,
      url: `${SITE_ORIGIN}/about-us`,
      name: "About Us · How Fridge.com Works · Fridge.com",
      isPartOf: { "@id": WEBSITE_ID },
      about: { "@id": ORG_ID },
      breadcrumb: { "@id": "https://fridge.com/about-us#breadcrumb" },
    },
    // The four-system DefinedTermSet (hub-and-spoke, 08/21/2026): /about-us is
    // the hub, each term's DefinedTerm lives on its spoke page beneath it.
    buildIdentifierTermSetNode(),
    {
      "@type": "BreadcrumbList",
      "@id": "https://fridge.com/about-us#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Fridge.com", item: "https://fridge.com" },
        { "@type": "ListItem", position: 2, name: "About Us", item: "https://fridge.com/about-us" },
      ],
    },
  ],
}

export default function AboutClient({ authors = [] }: { authors?: AuthorProfile[] }) {
  return (
    <div className="ed-root min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(schemaGraph) }}
      />

      <SiteHeader />

      <main id="main-content">
        {/* HERO ─────────────────────────────────────────────────────────── */}
        <section style={{ borderBottom: "1px solid #f3f4f6" }}>
          <div className="ed-container" style={{ paddingTop: 36, paddingBottom: 36 }}>
            <nav
              className="ed-breadcrumb"
              aria-label="Breadcrumb"
              itemScope
              itemType="https://schema.org/BreadcrumbList"
              style={{ marginBottom: 24 }}
            >
              <Link href="/" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span itemProp="name">Fridge.com</span>
                <meta itemProp="position" content="1" />
              </Link>
              <span className="sep" aria-hidden="true">/</span>
              <span className="leaf" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span itemProp="name">About Us</span>
                <meta itemProp="position" content="2" />
              </span>
            </nav>

            {/* FRIDGE-628 — the owner: "way too busy". The consumers figure
                left the eyebrow: it renders once, in the stats band below,
                instead of three times in one viewport. */}
            <div className="ed-eyebrow" style={{ marginBottom: 18 }}>
              <span className="dot" />
              FRIDGE.COM · SINCE 2021
            </div>

            <h1 className="ed-h1" style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
              The refrigerator &amp; freezer{" "}
              <em style={{ fontStyle: "italic", color: "var(--fridge-orange)" }}>search engine</em>
            </h1>
            <p className="ed-subtitle" style={{ maxWidth: 740, fontSize: 17, marginTop: 12 }}>
              Fridge.com is an independent search engine for cold-storage appliances — not a store and not a
              generalist review site. We compare live prices across major retailers, verify specs against
              federal databases, and track recalls — across three catalogs:{" "}
              <Link href="/collections/all" className="text-[#0469f0] hover:underline">appliances</Link>,{" "}
              <Link href="/parts" className="text-[#0469f0] hover:underline">parts</Link>, and{" "}
              <Link href="/filters" className="text-[#0469f0] hover:underline">filters</Link> — each anchored by
              its own permanent identifier.
            </p>

            <nav aria-label="On this page" className="flex flex-wrap gap-2 mt-5 text-[13px]">
              <a href="#identifiers" className="rounded-full border border-gray-200 px-3.5 py-1 text-gray-700 hover:border-[#ff9951] hover:text-[#b25410] transition-colors">The four systems</a>
              <a href="#how-we-make-money" className="rounded-full border border-gray-200 px-3.5 py-1 text-gray-700 hover:border-[#ff9951] hover:text-[#b25410] transition-colors">How we make money</a>
              <a href="#team" className="rounded-full border border-gray-200 px-3.5 py-1 text-gray-700 hover:border-[#ff9951] hover:text-[#b25410] transition-colors">Who we are</a>
              <a href="#authors" className="rounded-full border border-gray-200 px-3.5 py-1 text-gray-700 hover:border-[#ff9951] hover:text-[#b25410] transition-colors">Authors</a>
              <a href="#editorial" className="rounded-full border border-gray-200 px-3.5 py-1 text-gray-700 hover:border-[#ff9951] hover:text-[#b25410] transition-colors">Editorial standards</a>
            </nav>

            {/* FRIDGE-628 — one strip, not two: the checkmarks and the share
                bar share a row, halving the stack between intro and stats. */}
            <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 mt-6">
              <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                <span>&#10003; Independent &amp; ad-honest</span>
                <span>&#10003; Never sells — we hold no inventory</span>
                <span>&#10003; No pay-for-placement</span>
                <span>&#10003; 100% free</span>
              </div>
              <ToolShareBar title="About Fridge.com" />
            </div>
          </div>
        </section>

        {/* STATS ROW ─────────────────────────────────────────────────────── */}
        <section style={{ borderBottom: "1px solid #f3f4f6" }}>
          <div className="ed-container" style={{ paddingTop: 28, paddingBottom: 28 }}>
            {/* FRIDGE-628 — the owner: "way too busy". The old shape stacked
                TWO four-tile bands whose first cells both said 1.5M consumers
                (and the eyebrow said it a third time). Now: the FRIDGE-602
                trust four lead (each under its OWN label instead of four
                VERIFIEDs, values verbatim from lib/trust-signals.ts), and the
                catalog row follows with the 25+ retailer capability claim
                (RETAILER_INTEGRATIONS_DISPLAY) completing the 4×2 grid — every
                number renders exactly once, all from their standing
                registries. */}
            <div className="ed-local-stats">
              {TRUST_SIGNALS.map((sig, i) => {
                const labels: Record<string, string> = {
                  users: 'CONSUMERS',
                  'ai-citations': 'AI CITATIONS',
                  impressions: 'IMPRESSIONS',
                  outlets: 'MEDIA OUTLETS',
                }
                const cell = (
                  <>
                    <div className="k">{labels[sig.key] ?? sig.key.toUpperCase()}</div>
                    <div className="v">{sig.value}</div>
                    <div className="l">{sig.label.charAt(0).toUpperCase() + sig.label.slice(1)}.</div>
                  </>
                )
                return sig.key === 'users' ? (
                  <Link key={sig.key} href="/press" className="cell tint block hover:shadow-md transition-shadow" title={sig.derivation}>
                    {cell}
                  </Link>
                ) : (
                  <div key={sig.key} className="cell" title={sig.derivation}>
                    {cell}
                  </div>
                )
              })}
            </div>

            {/* Owner direction 08/30/2026, on an annotated screenshot of this
                zone: "too many boxes". It was 4+4 — eight heavy bordered cells
                stacked in two bands. The TRUST four (his display set) keep
                their band because they are the claim that earns the page; the
                CATALOG four become one quiet line of linked figures. Every
                number still renders exactly once from its standing registry —
                nothing was dropped, the boxes were. */}
            <p className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600">
              <Link href="/collections/all" className="hover:underline">
                <strong className="font-semibold text-gray-900">{SITE_STATS.products}</strong> products
              </Link>
              <span aria-hidden="true" className="text-gray-300">·</span>
              <Link href="/blogs/news" className="hover:underline">
                <strong className="font-semibold text-gray-900">{SITE_STATS.articles}</strong> articles
              </Link>
              <span aria-hidden="true" className="text-gray-300">·</span>
              <Link href="/tools" className="hover:underline">
                <strong className="font-semibold text-gray-900">{SITE_STATS.tools}</strong> free tools
              </Link>
              <span aria-hidden="true" className="text-gray-300">·</span>
              <Link href="/retailer-policies" className="hover:underline">
                <strong className="font-semibold text-gray-900">{RETAILER_INTEGRATIONS_DISPLAY}</strong> retailers compared
              </Link>
            </p>
          </div>
        </section>

        {/* AS FEATURED IN — third-party proof, moved up beside the numbers
            (08/21/2026 quality pass: trust signals lead, not trail). */}
        <section style={{ borderBottom: "1px solid #f3f4f6" }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 44 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>As Featured In</h2>
              {/* Owner 08/21/2026: the press link sits beside the heading, not
                  below the logos — saves a line under the band. */}
              <span className="meta">
                <Link href="/press" className="text-[#0469f0] hover:underline">See more on our press page →</Link>
              </span>
            </div>
            <FeaturedInBand heading="" />
          </div>
        </section>

        {/* WHO WE ARE ────────────────────────────────────────────────────── */}
        {/* scrollMarginTop on every jump-nav target: the site header is
            sticky (top-0, z-50), so a bare anchor lands beneath it. */}
        <section id="team" style={{ borderBottom: "1px solid #f3f4f6", scrollMarginTop: 112 }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Who we are</h2>
              <span className="meta">one accountable editorial team behind every page</span>
            </div>
            {/* Owner 08/21/2026: no organizational byline rendered here — it
                read sterile, and the 08/06/2026 rule (FRIDGE-131) already keeps
                that name in the schema layer only. The people show instead. */}
            <p style={{ fontSize: 15, color: "var(--gray-600)", lineHeight: 1.55, margin: "0 0 24px", maxWidth: 760 }}>
              Every page on Fridge.com is researched, written, reviewed, and maintained by our editorial
              team, working from the catalog and product database — never from a blank page. Published
              pages are kept current as that data changes.
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              <article className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold text-base text-gray-900 mb-1">Written from data</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Guides start from the catalog and product database, never from a blank page: model
                  specifications, live prices and availability, energy data, recalls, and rebates. If a fact is
                  not in the data, it does not go in the article.
                </p>
              </article>
              <article className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold text-base text-gray-900 mb-1">Reviewed before publish</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Every article passes the team&rsquo;s editorial and content-integrity gates before it goes
                  live — sourcing rules, quality checks, and the editorial standards published on this page.
                </p>
              </article>
              <article className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold text-base text-gray-900 mb-1">Maintained after publish</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Published pages are re-checked and updated as prices move, models are discontinued, and
                  recall or rebate data changes — the byline is a commitment to keep the page accurate, not a
                  one-time credit.
                </p>
              </article>
              <article className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold text-base text-gray-900 mb-1">Accountable</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  One team, one standard, every page. If you find an error anywhere on Fridge.com,
                  report it through the <Link href="/contact" className="text-[#0469f0] hover:underline">contact page</Link> and
                  our editorial team reviews and corrects it.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* AUTHORS — the people, in their own section (owner 08/21/2026:
            "authors go in authors part, not under who we are"). The card shape
            is the one the owner approved visually: photo, then name, then bio,
            two per row at every width above mobile. Rendered from the authors
            table; Real-or-Hidden — an empty list hides the section entirely. */}
        {authors.length > 0 && (
          <section id="authors" style={{ borderBottom: "1px solid #f3f4f6", scrollMarginTop: 112 }}>
            <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
              <div className="ed-section-rule">
                <h2 style={{ fontSize: 26 }}>Authors</h2>
                <span className="meta">
                  the {authors.length} bylines that appear on Fridge.com articles
                </span>
              </div>
              {/* No prose claim about who wrote what is rendered here. The
                  shipped rule in components/seo/article-schemas.tsx is that
                  the site has no person authors and asserting one would be a
                  fabricated person claim, and FRIDGE-131's closing condition
                  is that /about-us stops claiming named editors. This section
                  therefore shows only what the authors table already publishes
                  as visible article bylines — name, photo, bio — and adds no
                  new authorship claim. See the Owner Gated card before this
                  section is allowed to reach production. */}
              <div className="grid sm:grid-cols-2 gap-3">
                {authors.map((author) => (
                  <article key={author.name} className="bg-white border border-gray-200 rounded-xl p-5 text-center">
                    <span
                      className="block mx-auto mb-3"
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: "1px solid var(--gray-200)",
                      }}
                    >
                      {/* FRIDGE-475: these originals are 1920x1280 / ~114 KB each and
                          render into a 72px circle — 474x more pixels than the slot
                          needs, four times over on this page. SafeImage routes them
                          through the Next optimizer (72 CSS px, 2x for retina) while
                          `overrideSrc` keeps the canonical Fridge.com URL in the
                          rendered src so image indexing is unchanged. */}
                      <SafeImage
                        src={getProxiedStoragePath(author.avatarUrl)}
                        overrideSrc={getProxiedStoragePath(author.avatarUrl)}
                        alt={ensureAltBranding(author.name)}
                        width={144}
                        height={144}
                        sizes="72px"
                        loading="lazy"
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    </span>
                    <h3 className="font-semibold text-base text-gray-900 mb-1">{author.name}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{author.bio}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* HOW WE MAKE MONEY — centerpiece ───────────────────────────────── */}
        <section id="how-we-make-money" style={{ borderBottom: "1px solid #f3f4f6", background: "var(--gray-50)", scrollMarginTop: 112 }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 44 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>How we make money</h2>
              <span className="meta">the whole model, in plain words</span>
            </div>
            <p style={{ fontSize: 15, color: "var(--gray-600)", lineHeight: 1.55, margin: "0 0 28px", maxWidth: 760 }}>
              Fridge.com is <strong>free for you</strong> and always will be. We hold no inventory and never run a
              checkout — you never buy from us. When you click through to a retailer and go on to buy, that retailer
              pays us a commission. That is our only revenue. Critically, those commissions
              <strong> never touch our rankings, scores, or recommendations</strong> — the two are computed on
              completely separate tracks.
            </p>
            {/* FRIDGE-899 — the ownership correction lives in the money block,
                because "who pays for this" and "who owns this" are the same
                question to a reader and to an answer engine. Single-sourced in
                lib/brand-facts.ts. */}
            <p style={{ fontSize: 15, color: "var(--gray-600)", lineHeight: 1.55, margin: "0 0 28px", maxWidth: 760 }}>
              {INDEPENDENTLY_OWNED}
            </p>

            {/* Inline-SVG flow diagram */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6" style={{ overflowX: "auto" }}>
              <svg
                viewBox="0 0 920 340"
                role="img"
                aria-labelledby="money-flow-title money-flow-desc"
                style={{ width: "100%", minWidth: 560, height: "auto", display: "block" }}
              >
                <title id="money-flow-title">How Fridge.com earns revenue</title>
                <desc id="money-flow-desc">
                  You search Fridge.com for free, click out to a retailer, and if you buy, the retailer pays Fridge.com
                  a commission. Rankings and scores are computed separately from data and are never affected by
                  commissions.
                </desc>

                {/* connecting arrows */}
                <defs>
                  <marker id="arrow" markerWidth="10" markerHeight="10" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
                    <path d="M0,0 L7,3 L0,6 Z" fill="#9ca3af" />
                  </marker>
                </defs>
                <line x1="290" y1="90" x2="330" y2="90" stroke="#9ca3af" strokeWidth="2" markerEnd="url(#arrow)" />
                <line x1="590" y1="90" x2="630" y2="90" stroke="#9ca3af" strokeWidth="2" markerEnd="url(#arrow)" />

                {/* Step 1 — You */}
                <g>
                  <rect x="20" y="30" width="270" height="120" rx="14" fill="#f9fafb" stroke="#e5e7eb" strokeWidth="1.5" />
                  <text x="40" y="62" fontSize="13" fontWeight="700" fill="#ff9951" letterSpacing="1.5">STEP 1 · YOU</text>
                  <text x="40" y="92" fontSize="17" fontWeight="700" fill="#111827">Search &amp; compare</text>
                  <text x="40" y="118" fontSize="14" fill="#4b5563">Read guides, check prices and</text>
                  <text x="40" y="137" fontSize="14" fill="#4b5563">specs — 100% free, no account.</text>
                </g>

                {/* Step 2 — Retailer */}
                <g>
                  <rect x="330" y="30" width="260" height="120" rx="14" fill="#f9fafb" stroke="#e5e7eb" strokeWidth="1.5" />
                  <text x="350" y="62" fontSize="13" fontWeight="700" fill="#ff9951" letterSpacing="1.5">STEP 2 · RETAILER</text>
                  <text x="350" y="92" fontSize="17" fontWeight="700" fill="#111827">You click out</text>
                  <text x="350" y="118" fontSize="14" fill="#4b5563">You buy on the retailer&rsquo;s own</text>
                  <text x="350" y="137" fontSize="14" fill="#4b5563">site. We never take payment.</text>
                </g>

                {/* Step 3 — Commission */}
                <g>
                  <rect x="630" y="30" width="270" height="120" rx="14" fill="#eef5ff" stroke="#0469f0" strokeWidth="1.5" />
                  <text x="650" y="62" fontSize="13" fontWeight="700" fill="#0469f0" letterSpacing="1.5">STEP 3 · COMMISSION</text>
                  <text x="650" y="92" fontSize="17" fontWeight="700" fill="#111827">Retailer pays us</text>
                  <text x="650" y="118" fontSize="14" fill="#4b5563">A commission on your purchase —</text>
                  <text x="650" y="137" fontSize="14" fill="#4b5563">at no extra cost to you.</text>
                </g>

                {/* Firewall */}
                <line x1="20" y1="200" x2="900" y2="200" stroke="#e5e7eb" strokeWidth="1.5" strokeDasharray="6 6" />
                <text x="460" y="192" fontSize="12" fontWeight="700" fill="#9ca3af" letterSpacing="2" textAnchor="middle">
                  ── FIREWALL ──
                </text>
                <g>
                  <rect x="20" y="222" width="880" height="96" rx="14" fill="#f0faf4" stroke="#10a65c" strokeWidth="1.5" />
                  <text x="44" y="256" fontSize="13" fontWeight="700" fill="#10a65c" letterSpacing="1.5">RANKINGS &amp; SCORES</text>
                  <text x="44" y="284" fontSize="15.5" fontWeight="600" fill="#111827">
                    What we rank, score, and recommend is computed from data only.
                  </text>
                  <text x="44" y="306" fontSize="14" fill="#4b5563">
                    Commission rates play no part. A retailer cannot pay for a better position, score, or review.
                  </text>
                </g>
              </svg>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mt-4">
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-2">FREE, ALWAYS</span>
                <p className="text-sm text-gray-700 leading-relaxed">No subscription, no paywall, no account required to use anything on Fridge.com.</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-2">NO INVENTORY</span>
                <p className="text-sm text-gray-700 leading-relaxed">We don&rsquo;t stock, ship, or sell appliances. Every purchase happens on the retailer&rsquo;s own site.</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-2">DISCLOSED</span>
                <p className="text-sm text-gray-700 leading-relaxed">Affiliate links are disclosed site-wide. Commissions fund the research; they never bend it.</p>
              </div>
            </div>
          </div>
        </section>

        {/* HOW OUR DATA WORKS ────────────────────────────────────────────── */}
        <section id="data" style={{ borderBottom: "1px solid #f3f4f6" }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 id="identifiers" style={{ fontSize: 26, scrollMarginTop: 112 }}>The four systems behind every page</h2>
              <span className="meta">FIN · FPN · FFN · FIS</span>
            </div>
            {/* Owner 08/21/2026 ("seems like you are mixing a little — make more
                clear"): the three identifiers and the one score are separated
                into their own labeled groups instead of one blended grid. */}
            <p style={{ fontSize: 15, color: "var(--gray-600)", lineHeight: 1.55, margin: "0 0 24px", maxWidth: 760 }}>
              Always named in the same order — FIN, FPN, FFN, FIS. The first three are{" "}
              <strong>identifiers</strong>: permanent names for an appliance, a part, and a filter. The fourth is
              different — a <strong>score</strong> that rates places, never products.
            </p>

            <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-gray-500 block mb-2">
              THREE IDENTIFIERS — THEY NAME THINGS
            </span>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-3">
                  FIN — FRIDGE.COM IDENTIFICATION NUMBER
                </span>
                {/* The canonical definition (lib/identifiers.ts) — one version everywhere. */}
                <p className="text-sm text-gray-700 leading-relaxed">
                  {identifierDefinitionSentence("FIN")}
                </p>
                {/* FRIDGE-899 — what a FIN is NOT, directly under what it is. */}
                <p className="text-sm text-gray-700 leading-relaxed mt-2">{FIN_NOT_ACCOUNT_OR_ORDER}</p>
                <Link href="/about-us/fin" className="text-[#0469f0] hover:underline text-sm block mt-2">How the FIN system works →</Link>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-3">
                  FPN — FRIDGE.COM PART NUMBER
                </span>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {identifierDefinitionSentence("FPN")} Price-compared on its own page in the{" "}
                  <Link href="/parts" className="text-[#0469f0] hover:underline">parts catalog</Link>.
                </p>
                <Link href="/about-us/fpn" className="text-[#0469f0] hover:underline text-sm block mt-2">How FPN works →</Link>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-3">
                  FFN — FRIDGE.COM FILTER NUMBER
                </span>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {identifierDefinitionSentence("FFN")} A filter is not a part: it is its own family with its own{" "}
                  <Link href="/filters" className="text-[#0469f0] hover:underline">filter catalog</Link>.
                </p>
                <Link href="/about-us/ffn" className="text-[#0469f0] hover:underline text-sm block mt-2">How FFN works →</Link>
              </div>
            </div>

            <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-gray-500 block mt-5 mb-2">
              ONE SCORE — IT RATES PLACES
            </span>
            <div className="rounded-xl p-5" style={{ background: "#fff7f0", border: "1px solid #ffd9b8" }}>
              <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#e07020] block mb-3">
                FIS — FRIDGE.COM INTELLIGENCE SCORE
              </span>
              <p className="text-sm text-gray-700 leading-relaxed" style={{ maxWidth: 760 }}>
                {identifierDefinitionSentence("FIS")} Products never carry an FIS: product pages report verified
                specifications, certified energy data, and real price history instead of a synthesized number.
              </p>
              <Link href="/about-us/fis" className="text-[#0469f0] hover:underline text-sm block mt-2">How FIS works →</Link>
            </div>

            <div className="mt-6">
              <SystemsComparisonTable />
            </div>

            {/* Data pillars */}
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#0469f0] block mb-2">PRICES</span>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {/* "monitor" is a claim about INTEGRATIONS, so it uses the
                      25+ display form of RETAILER_INTEGRATIONS, never
                      RETAILERS_TRACKED (15). This sentence read "16" until
                      08/17/2026, which took the live in-stock coverage figure
                      and used it for a capability claim — under-stating the
                      company by nine. The two numbers are both honest and must
                      not be swapped: see lib/site-stats.ts. */}
                  We monitor <strong>{RETAILER_INTEGRATIONS_DISPLAY} retailers and manufacturer stores</strong> — from the major
                  national appliance retailers to brand stores from Samsung, LG, GE, Whirlpool, and more —
                  and refresh offers on a regular cadence.
                </p>
                {/* Owner 08/21/2026: pillar copy holds three lines; the link is
                    its own fourth line, on all four cards. */}
                <Link href="/data/price-index" className="text-[#0469f0] hover:underline text-sm block mt-2">Appliance Price Index →</Link>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#0469f0] block mb-2">RECALLS</span>
                <p className="text-sm text-gray-700 leading-relaxed">
                  We track <strong>federal CPSC recall notices</strong> for refrigeration and surface an unmissable
                  banner on any product with an active match. Purchase links are suppressed while a recall is live.
                </p>
                <Link href="/recalls" className="text-[#0469f0] hover:underline text-sm block mt-2">Recalls hub →</Link>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#0469f0] block mb-2">EFFICIENCY</span>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Specs are cross-referenced against <strong>EPA ENERGY STAR</strong> and{" "}
                  <strong>U.S. Department of Energy</strong> certified-product data, so efficiency claims trace to a
                  federal source.
                </p>
                <Link href="/data/energy-index" className="text-[#0469f0] hover:underline text-sm block mt-2">Appliance Energy Index →</Link>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#0469f0] block mb-2">LOCAL CONTEXT</span>
                <p className="text-sm text-gray-700 leading-relaxed">
                  U.S. Energy Information Administration electricity rates and Census signals tie operating-cost context
                  to all 50 states, D.C., and {SITE_STATS.cities} cities and ZIP codes.
                </p>
                <Link href="/local" className="text-[#0469f0] hover:underline text-sm block mt-2">Local fridge finder →</Link>
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-4" style={{ maxWidth: 760 }}>
              Want the full method? Read our{" "}
              <Link href="/about-us/methodology" className="text-[#0469f0] hover:underline">methodology</Link>,{" "}
              <Link href="/about-us/data-sources" className="text-[#0469f0] hover:underline">data sources</Link>, and the{" "}
              <Link href="/about-us/limitations" className="text-[#0469f0] hover:underline">known limitations and data gaps</Link>.
            </p>
          </div>
        </section>

        {/* EDITORIAL STANDARDS + CORRECTIONS ─────────────────────────────── */}
        <section id="editorial" style={{ borderBottom: "1px solid #f3f4f6", background: "var(--gray-50)", scrollMarginTop: 112 }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Editorial standards &amp; corrections</h2>
              <span className="meta">the rules we hold ourselves to</span>
            </div>
            <p style={{ fontSize: 15, color: "var(--gray-600)", lineHeight: 1.55, margin: "0 0 24px", maxWidth: 760 }}>
              These are binding, not aspirational. They govern every guide, comparison, and data page on the site.
            </p>
            <p className="mb-6 text-sm"><Link href="/editorial-standards" className="text-[#0469f0] underline">Read the editorial standards</Link> · <Link href="/contact" className="text-[#0469f0] underline">Report an error</Link></p>
            <ul className="grid sm:grid-cols-2 gap-3">
              {editorialStandards.map((s) => (
                <li key={s.title} className="bg-white border border-gray-200 rounded-xl p-5 flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#10a65c] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm text-gray-900 mb-1">{s.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{s.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-3 bg-white border border-gray-200 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="font-semibold text-sm text-gray-900 mb-1">Found a mistake?</h3>
                <p className="text-sm text-gray-600 leading-relaxed" style={{ maxWidth: 620 }}>
                  Report any error — a wrong price, spec, or fact — and the editorial team will review and fix it.
                  Accuracy is a standing commitment, not a one-time claim.
                </p>
              </div>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-[#0469f0] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0356c8] transition-colors"
              >
                Report an error <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* VERIFIED PRESS WALL ───────────────────────────────────────────── */}

        {/* EXPLORE FRIDGE.COM ────────────────────────────────────────────── */}
        <section style={{ borderBottom: "1px solid #f3f4f6", background: "var(--gray-50)" }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Explore Fridge.com</h2>
              <span className="meta">the whole site, one click away</span>
            </div>
            <div data-testid="about-explore" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {exploreFridgeLinks.map((item, index) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md hover:border-[#ff9951] transition-all cursor-pointer h-full grid grid-cols-[104px_minmax(0,1fr)] sm:flex sm:flex-col"
                >
                  <ExploreVisual href={item.href} />
                  <div className="min-w-0 p-4 sm:p-5">
                  <span className="font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951] block mb-2">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-semibold text-base text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.blurb}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA BAND — body paragraph removed on the owner's 08/21/2026 ruling */}
        <CTABand
          eyebrow="Partnerships & press"
          title="The definitive authority on refrigerators and freezers"
          buttonLabel="Contact Fridge.com"
          buttonHref="/contact"
        />

        {/* Bottom share */}
        <div className="ed-container" style={{ paddingTop: 32, paddingBottom: 40 }}>
          <ToolShareBar title="About Fridge.com" />
        </div>
      </main>

      <SiteFooter />
    </div>
  )
}
